import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { useTable, useSortBy, useGroupBy } from 'react-table';
import { useMemo, useState } from 'react';
import InfoOverlay from '../../../components/InfoOverlay';
import usePaginatedTable from '../../../hooks/UsePaginatedTable';
import PaginationSection from '../../../components/pagination/PaginationSection';

function StartlistTable({
  startlist,
  status,
  setChosenParticipant,
  competitionRacingNumberClassInterval,
  onRemove,
  onEdit,
  setBatchSelectedParticipants,
  batchSelectedParticipants,
  rowsPerPage,
}) {
  // Pagination
  const [page, setPage] = useState(1);
  const { slice, range } = usePaginatedTable(startlist, page, rowsPerPage);

  const data = useMemo(() => slice, [startlist, slice]);
  const columns = useMemo(
    () => [
      {
        id: 'cb-column',
        accessor: 'participantInformation',
        Header: () => (

          <>
            {(status === 'Kommande' || status === 'Pågående') && (
              <input
                type='checkbox'
                checked={startlist.length === batchSelectedParticipants.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    const allParticipants = startlist.map((p) => p);
                    setBatchSelectedParticipants(allParticipants);
                  } else {
                    setBatchSelectedParticipants([]);
                  }
                }}
              />
            )}
          </>
        ),
        className: 'p-3 ps-2',
        style: { width: '100px' },
        Cell: (props) => (
          <>
            <input
              type='checkbox'
              checked={batchSelectedParticipants.some(
                (p) => p.racingNumber === props.row.original.racingNumber
              )}
              disabled={status !== 'Kommande' && status !== 'Pågående'}
              onChange={() => {
                let newArr = [...batchSelectedParticipants];

                if (
                  newArr.some(
                    (e) => e.racingNumber === props.row.original.racingNumber
                  )
                ) {
                  newArr = newArr.filter(
                    (p) => p.racingNumber !== props.row.original.racingNumber
                  );
                } else {
                  newArr = [...newArr, props.row.original];
                }

                setBatchSelectedParticipants(newArr);
              }}
            />
          </>
        ),
        disableSortBy: true,
      },
      {
        Header: 'Tävlande',
        accessor: 'racingNumber',
        className: 'p-3 ps-2',
        style: { width: '30%' },
        Cell: (props) => (
          <div className='d-flex align-items-center'>
            <span className='bg-secondary-black text-white p-1 me-2'>
              {props.row.original.racingNumber}
            </span>
            <InfoOverlay
              name={props.row.original.name}
              info={props.row.original.participantDetails}
              unRegistered={props.row.original.unRegistered}
              participantInfo={true}
            />
          </div>
        ),
      },
      {
        Header: 'Klubb',
        accessor: 'clubAffiliation',
        className: 'p-3 ps-2',
        style: { width: '18%' },
        Cell: ({ cell: { value } }) => (
          <div className='d-flex gap-1'>{value ? value : 'Ej angett'}</div>
        ),
      },
      {
        Header: 'Tävlingsklass',
        accessor: 'raceClassString',
        className: 'p-3 ps-2',
        style: { width: '10%' },
        Cell: ({ cell: { value } }) => (
          <div className='d-flex gap-1'>
            {value
              ? value === 'women'
                ? 'Dam'
                : value.charAt(0).toUpperCase() + value.slice(1)
              : 'Ej angett'}
          </div>
        ),
      },
      {
        Header: 'Godkänd besiktning',
        accessor: 'approvedInspection',
        className: 'p-3 ps-2',
        style: { width: '10%' },
        Cell: ({ cell: { value } }) => (
          <div className='d-flex gap-1'>
            {value ? (
              <span className='bg-success p-1'>Ja</span>
            ) : (
              <span className='bg-danger p-1'>Nej</span>
            )}
          </div>
        ),
      },
      {
        Header: '',
        accessor: 'participantId',
        className: 'p-3',
        style: { width: '35%' },
        Cell: (props) => (
          <div className='d-flex flex-column justify-content-end flex-md-row gap-2 col-md-auto col-12'>
            {competitionRacingNumberClassInterval && (
              <Button
                variant='outline-primary'
                className='d-flex align-items-center'
                onClick={() => {
                  if (status !== 'Avslutad') {
                    setChosenParticipant({ id: props.row.original.participantId, racingNumber: props.row.original.racingNumber, raceClassString: props.row.original.raceClassString, email: props.row.original.participantDetails.email })
                    onEdit();
                  }
                }}
                disabled= {status === 'Avslutad' } // {status === 'Avslutad' || props.row.original.numberSetManually}
              >
                <span className='material-symbols-outlined me-2'>edit</span>
                Redigera startnummer
              </Button>
            )}
            <Button
              variant='outline-primary'
              className='d-flex align-items-center'
              onClick={() => {
                if (status !== 'Avslutad') {
                  setChosenParticipant({ id: props.row.original.participantId, racingNumber: props.row.original.racingNumber, email: props.row.original.participantDetails.email })
                  onRemove();
                }
              }}
              disabled={status === 'Avslutad'}
            >
              <span className='material-symbols-outlined me-2'>delete</span>
              Ta bort
            </Button>
          </div>
        ),
      },
    ],
    [batchSelectedParticipants, startlist]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      useGroupBy,
      useSortBy
    );

  return (
    <div className='d-flex flex-column w-100'>
      <Table
        {...getTableProps()}
        style={{
          borderCollapse: 'separate',
          borderSpacing: '0 0.5rem',
          width: '100%',
        }}
        className='table-borderless'
        hover
      >
        <thead className='bg-divider'>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                className='bg-white'
                style={{ borderStyle: 'none' }}
              >
                {row.cells.map((cell) => {
                  return (
                    <td
                      {...cell.getCellProps()}
                      className={cell.column.className}
                      style={cell.column.style}
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
      <PaginationSection
        range={range}
        slice={slice}
        setPage={setPage}
        page={page}
      />
    </div>
  );
}

export default StartlistTable;
